import { useState, useMemo } from "react";
import { codigoLetra, planSimple, planDoble, AQL_DISPONIBLES } from "./aqlIRAM15";

// ─────────────────────────────────────────────────────────────────────────
// Calculadora de AQL (muestreo por atributos, IRAM 15 / ISO 2859-1).
// Colgada de ControlCalidadPiso.js — se abre como una vista más de ese
// módulo, no es un módulo aparte (mismo criterio: es una herramienta que
// usa Calidad dentro del flujo de control, no una entidad de ALQOS con
// su propio listado/wiring).
//
// onGuardarResultado(resultado): si se pasa, aparece el botón "Usar este
// resultado" que le entrega a quien lo llama un objeto limpio, listo para
// guardar dentro del control de piso actual y, más adelante, para que la
// generación del Certificado de Calidad (COA) lo incluya en el PDF.
// ─────────────────────────────────────────────────────────────────────────

const NIVELES = ["S-1", "S-2", "S-3", "S-4", "I", "II", "III"];
const SEVERIDADES = [
  { val: "normal", label: "Normal", disponible: true },
  { val: "tightened", label: "Rigurosa (tightened)", disponible: false },
  { val: "reduced", label: "Reducida (reduced)", disponible: false },
];
const TIPOS_MUESTREO = [
  { val: "simple", label: "Simple", disponible: true },
  { val: "doble", label: "Doble", disponible: false },
];

export default function CalculadoraAQL({ oscuro, onVolver, onGuardarResultado }) {
  const [tamanoLote, setTamanoLote] = useState("");
  const [nivel, setNivel] = useState("II");
  const [aql, setAql] = useState(2.5);
  const [severidad, setSeveridad] = useState("normal");
  const [tipoMuestreo, setTipoMuestreo] = useState("simple");
  const [defectosEncontrados, setDefectosEncontrados] = useState("");

  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "12px", color: oscuro ? "#9aabd4" : "#555", fontWeight: "600", display: "block", marginTop: "12px" };
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };

  const letra = useMemo(() => {
    const n = parseInt(tamanoLote, 10);
    if (!n || n < 1) return null;
    return codigoLetra(n, nivel);
  }, [tamanoLote, nivel]);

  const plan = useMemo(() => {
    if (!letra) return null;
    if (tipoMuestreo === "doble") return planDoble();
    return planSimple(letra, aql, severidad);
  }, [letra, aql, severidad, tipoMuestreo]);

  const decision = useMemo(() => {
    if (!plan?.disponible || defectosEncontrados === "") return null;
    const d = parseInt(defectosEncontrados, 10);
    if (isNaN(d)) return null;
    if (d <= plan.Ac) return "aceptado";
    return "rechazado";
  }, [plan, defectosEncontrados]);

  const resultado = useMemo(() => {
    if (!plan?.disponible) return null;
    return {
      tamanoLote: parseInt(tamanoLote, 10),
      nivel, letra, aql, severidad, tipoMuestreo,
      n: plan.n, Ac: plan.Ac, Re: plan.Re,
      defectosEncontrados: defectosEncontrados !== "" ? parseInt(defectosEncontrados, 10) : null,
      decision,
      norma: "IRAM 15 (ISO 2859-1)",
      fecha: new Date().toISOString(),
    };
  }, [plan, tamanoLote, nivel, letra, aql, severidad, tipoMuestreo, defectosEncontrados, decision]);

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", gap: "12px", position: "sticky", top: 0, zIndex: 100 }}>
        <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
        <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>📐 Calculadora AQL — IRAM 15</span>
      </div>

      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "1.5rem auto 0" }}>
        <div style={{ background: oscuro ? "#3a2a05" : "#fff8e1", border: "1px solid #f59e0b", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px" }}>
          <p style={{ fontSize: "12px", color: "#f59e0b", fontWeight: "600", margin: 0 }}>
            ⚠️ Solo Normal + Simple están verificados contra la norma. Rigurosa, Reducida y Doble muestreo están deshabilitados a propósito hasta cargar esos valores desde IRAM 15/ISO 2859-1 original — no se inventó ningún número.
          </p>
        </div>

        <div style={card}>
          <span style={lbl}>Tamaño del lote *</span>
          <input type="number" value={tamanoLote} onChange={e => setTamanoLote(e.target.value)} style={inp} placeholder="Ej: 5000" />

          <span style={lbl}>Nivel de inspección</span>
          <select value={nivel} onChange={e => setNivel(e.target.value)} style={inp}>
            {NIVELES.map(n => <option key={n} value={n}>{n}{n === "II" ? " (general, el que se usa por defecto)" : ""}</option>)}
          </select>

          <span style={lbl}>AQL (nivel de calidad aceptable, %)</span>
          <select value={aql} onChange={e => setAql(parseFloat(e.target.value))} style={inp}>
            {AQL_DISPONIBLES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>

          <span style={lbl}>Severidad</span>
          <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
            {SEVERIDADES.map(s => (
              <button key={s.val} disabled={!s.disponible} onClick={() => s.disponible && setSeveridad(s.val)}
                title={!s.disponible ? "Tabla pendiente de verificar — ver aviso arriba" : ""}
                style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: s.disponible ? "pointer" : "not-allowed", fontWeight: "600", opacity: s.disponible ? 1 : 0.4, border: severidad === s.val ? "2px solid #1C3FAA" : `1px solid ${borde}`, background: severidad === s.val ? "rgba(28,63,170,.1)" : cardBg, color: severidad === s.val ? "#1C3FAA" : texto }}>{s.label}</button>
            ))}
          </div>

          <span style={lbl}>Tipo de muestreo</span>
          <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
            {TIPOS_MUESTREO.map(t => (
              <button key={t.val} disabled={!t.disponible} onClick={() => t.disponible && setTipoMuestreo(t.val)}
                title={!t.disponible ? "Tabla pendiente de verificar — ver aviso arriba" : ""}
                style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: t.disponible ? "pointer" : "not-allowed", fontWeight: "600", opacity: t.disponible ? 1 : 0.4, border: tipoMuestreo === t.val ? "2px solid #1C3FAA" : `1px solid ${borde}`, background: tipoMuestreo === t.val ? "rgba(28,63,170,.1)" : cardBg, color: tipoMuestreo === t.val ? "#1C3FAA" : texto }}>{t.label}</button>
            ))}
          </div>
        </div>

        {letra && (
          <div style={card}>
            <h3 style={{ fontSize: "14px", fontWeight: "700", color: "#1C3FAA", margin: "0 0 10px" }}>Resultado</h3>
            <p style={{ fontSize: "13px", color: texto, margin: "0 0 6px" }}>Código de letra: <b>{letra}</b></p>
            {plan?.disponible ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginTop: "10px" }}>
                  {[["Tamaño de muestra (n)", plan.n, "#1C3FAA"], ["Ac (aceptación)", plan.Ac, "#22c55e"], ["Re (rechazo)", plan.Re, "#e63946"]].map(([lab, val, col]) => (
                    <div key={lab} style={{ background: oscuro ? "#091840" : "#f8f9fa", borderRadius: "10px", padding: "10px", textAlign: "center", border: `1px solid ${borde}` }}>
                      <p style={{ fontSize: "20px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
                      <p style={{ fontSize: "10px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
                    </div>
                  ))}
                </div>

                <span style={lbl}>Defectos encontrados en la muestra</span>
                <input type="number" value={defectosEncontrados} onChange={e => setDefectosEncontrados(e.target.value)} style={inp} placeholder="Cargalo después de inspeccionar la muestra" />

                {decision && (
                  <div style={{ marginTop: "12px", padding: "12px 16px", borderRadius: "10px", background: decision === "aceptado" ? "#22c55e22" : "#e6394622", border: `1px solid ${decision === "aceptado" ? "#22c55e" : "#e63946"}` }}>
                    <p style={{ fontSize: "14px", fontWeight: "700", color: decision === "aceptado" ? "#22c55e" : "#e63946", margin: 0 }}>
                      {decision === "aceptado" ? "✅ LOTE ACEPTADO" : "❌ LOTE RECHAZADO"}
                    </p>
                  </div>
                )}

                {onGuardarResultado && resultado && (
                  <button onClick={() => onGuardarResultado(resultado)} style={{ width: "100%", marginTop: "14px", padding: "12px", background: "#1C3FAA", color: "white", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}>
                    Usar este resultado en el control
                  </button>
                )}
              </>
            ) : (
              <p style={{ fontSize: "12px", color: "#e63946" }}>{plan?.motivo || "No se pudo calcular el plan."}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
